import { useMemo, useState } from "react";
import type { ReactNode } from "react";

export type Column<T> = {
  key: string;
  header: ReactNode;
  /** Render the cell. */
  cell: (row: T) => ReactNode;
  /** Mono, right-aligned, tabular. Use for every number. */
  numeric?: boolean;
  /** Provide to make the column sortable. Return a string or number. */
  sortBy?: (row: T) => string | number;
  width?: string;
};

type Dir = "asc" | "desc";

const SortIcon = ({ dir }: { dir?: Dir }) => (
  <svg className="th-sort-icon" width="11" height="11" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    {dir === "asc" ? <path d="m6 14 6-6 6 6" />
      : dir === "desc" ? <path d="m6 10 6 6 6-6" />
      : <path d="m7 10 5-5 5 5M7 14l5 5 5-5" />}
  </svg>
);

/**
 * Sortable, selectable table.
 *
 * Sorting is done here so a caller with an in-memory list gets it for free.
 * For server-side paging, sort upstream and omit `sortBy` on the columns.
 */
export function DataTable<T>({
  columns, rows, rowKey, selectable, selected, onSelectedChange,
  bulkActions, empty, footer, onRowClick,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  selectable?: boolean;
  selected?: string[];
  onSelectedChange?: (keys: string[]) => void;
  bulkActions?: ReactNode;
  empty?: ReactNode;
  footer?: ReactNode;
  onRowClick?: (row: T) => void;
}) {
  const [sort, setSort] = useState<{ key: string; dir: Dir } | null>(null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortBy) return rows;
    const by = col.sortBy;
    return [...rows].sort((a, b) => {
      const x = by(a), y = by(b);
      const cmp = typeof x === "number" && typeof y === "number"
        ? x - y
        : String(x).localeCompare(String(y));
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [rows, sort, columns]);

  const sel = selected ?? [];
  const allKeys = sorted.map(rowKey);
  const allSelected = allKeys.length > 0 && allKeys.every((k) => sel.includes(k));

  const toggleAll = () => onSelectedChange?.(allSelected ? [] : allKeys);
  const toggleOne = (k: string) =>
    onSelectedChange?.(sel.includes(k) ? sel.filter((x) => x !== k) : [...sel, k]);

  const headerClick = (c: Column<T>) => {
    if (!c.sortBy) return;
    setSort((s) =>
      s?.key === c.key
        ? { key: c.key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key: c.key, dir: "asc" });
  };

  if (rows.length === 0 && empty) return <>{empty}</>;

  return (
    <div className="panel panel-flush">
      {selectable && sel.length > 0 && (
        <div className="bulkbar">
          <span><b className="num">{sel.length}</b> selected</span>
          {bulkActions}
          <button className="btn btn-ghost" style={{ marginLeft: "auto" }}
                  onClick={() => onSelectedChange?.([])}>Clear</button>
        </div>
      )}
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              {selectable && (
                <th className="table-select">
                  <label className="check">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                    <span className="sr-only">Select all rows</span>
                  </label>
                </th>
              )}
              {columns.map((c) => {
                const active = sort?.key === c.key;
                return (
                  <th key={c.key} style={{ width: c.width }}
                      className={c.numeric ? "cell-right" : undefined}
                      aria-sort={active ? (sort!.dir === "asc" ? "ascending" : "descending") : undefined}>
                    {c.sortBy ? (
                      <button className="th-sort" onClick={() => headerClick(c)}>
                        {c.header}<SortIcon dir={active ? sort!.dir : undefined} />
                      </button>
                    ) : c.header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const k = rowKey(row);
              return (
                <tr key={k} data-selected={sel.includes(k) || undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    style={onRowClick ? { cursor: "pointer" } : undefined}>
                  {selectable && (
                    <td className="table-select" onClick={(e) => e.stopPropagation()}>
                      <label className="check">
                        <input type="checkbox" checked={sel.includes(k)} onChange={() => toggleOne(k)} />
                        <span className="sr-only">Select row</span>
                      </label>
                    </td>
                  )}
                  {columns.map((c) => (
                    <td key={c.key} className={c.numeric ? "cell-num" : undefined}>
                      {c.cell(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {footer}
    </div>
  );
}

export function Pagination({ page, pageSize, total, onPageChange }: {
  page: number; pageSize: number; total: number; onPageChange: (p: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="pagination">
      <span className="pagination-info">
        <b>{from}</b> to <b>{to}</b> of <b>{total}</b>
      </span>
      <div className="row-tight">
        <button className="btn btn-secondary" disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}>Previous</button>
        <span className="pagination-info">Page <b>{page}</b> of <b>{pages}</b></span>
        <button className="btn btn-secondary" disabled={page >= pages}
                onClick={() => onPageChange(page + 1)}>Next</button>
      </div>
    </div>
  );
}
